import { logger } from "@/modules/logger";
import {
  AutoCleanupDecision,
  AutoCleanupInput,
  AutoCleanupResult,
  AutoCleanupNoteState,
} from "./auto-cleanup-policy.types";
import { objectModule } from "@/modules/object";

/**
 * Determines if a note is in original state.
 * (We use this to determine if a note should be deleted permanently or moved to trash.)
 */
const isOriginalState = ({
  modelVersion,
  hasQueuedUpdates,
}: Pick<AutoCleanupNoteState, "modelVersion" | "hasQueuedUpdates">): boolean => {
  if (hasQueuedUpdates) return false;

  return modelVersion <= 1;
};

/**
 * Determines if a note is in original state.
 * (We use this to determine if a note should be deleted permanently or moved to trash.)
 */
const _shouldAutoCleanUp = (input: AutoCleanupInput): AutoCleanupDecision => {
  const { editor, note, runtime } = input;

  if (editor.isReadOnly) return { result: null };
  if (!editor.isNoteEmpty) return { result: null };

  if (note.isDeleted) return { result: null };
  if (note.isTrashed) return { result: null };
  if (!note.isOwnedByMe) return { result: null };
  if (!note.isContentLoaded) return { result: null };
  if (note.hasOpenTab) return { result: null };

  if (runtime.isAudioRecordingActive) return { result: null };
  if (runtime.hasRecording) return { result: null };
  if (runtime.hasSuggestion) return { result: null };
  if (runtime.hasSource) return { result: null };

  /**
   * If isAutoDeletable is true, always move to trash instead of permanent deletion
   */
  if (runtime.isAutoDeletable) {
    return { result: AutoCleanupResult.MoveToTrash };
  }

  /**
   * Otherwise, follow normal logic:
   * - If it is in the original state (untouched) we can delete it permanently
   * - Otherwise, we trash it
   */
  if (
    isOriginalState({ modelVersion: note.modelVersion, hasQueuedUpdates: note.hasQueuedUpdates })
  ) {
    return { result: AutoCleanupResult.DeletePermanently };
  }

  return { result: AutoCleanupResult.MoveToTrash };
};

/**
 * Determines if a note should be automatically cleaned up.
 */
export const shouldAutoCleanUp = (input: AutoCleanupInput): AutoCleanupDecision => {
  const result = _shouldAutoCleanUp(input);

  logger.info({
    message: `[shouldAutoCleanUp] ${input.id}`,
    info: objectModule.safeAsJson({ input, result }),
  });

  return result;
};
